import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Phone, MessageCircle, ArrowRight, Plane, Car, Users, Baby, CreditCard, Clock, Radar, MapPinCheck, PhoneCall, Luggage } from 'lucide-react';
import PageSEO from '@/components/PageSEO';
import { BUSINESS } from '@/lib/constants';
import { webPage, service, breadcrumb } from '@/lib/schemas';

const HERO_IMG = 'https://images.unsplash.com/photo-1548237326-bf241b55a874?fm=jpg&q=85&w=1400&auto=format&fit=crop';

const AIRPORTS = [
  {
    key: 'ewr',
    code: 'EWR',
    name: 'Newark Liberty',
    price: 70,
    drive: '30–45 min',
    desc: 'Our most popular airport route — quick access via the Goethals Bridge and I-95.',
  },
  {
    key: 'jfk',
    code: 'JFK',
    name: 'John F. Kennedy',
    price: 100,
    drive: '50–70 min',
    desc: 'Pre-booked rides across the Verrazzano and Belt Parkway. Allow extra time during rush hour.',
  },
  {
    key: 'lga',
    code: 'LGA',
    name: 'LaGuardia',
    price: 115,
    drive: '55–75 min',
    desc: 'The furthest airport from Staten Island. Best with early or late timing to avoid traffic.',
  },
];

const PICKUP_STEPS = [
  {
    icon: Plane,
    title: 'Share Your Flight Details',
    desc: 'Provide your airline, flight number, and date when you book your return ride.',
  },
  {
    icon: Radar,
    title: 'We Track Your Flight',
    desc: 'Your driver monitors your flight in real time and heads to the airport as you land.',
  },
  {
    icon: PhoneCall,
    title: 'Driver Meets You Curbside',
    desc: 'You\'ll get a call or message with the exact meeting point at arrivals.',
  },
  {
    icon: Luggage,
    title: 'On Your Way Home',
    desc: 'Sit back, relax — you\'re headed to Staten Island.',
  },
];

const FAQS = [
  {
    q: 'How much is a ride to Newark, JFK, or LaGuardia?',
    a: 'Rides to Newark start at $70, JFK at $100, and LaGuardia at $115. All fares include tolls. Final price depends on pickup location and time of day.',
  },
  {
    q: 'How far in advance should I book?',
    a: 'We prefer at least one day before your trip, but you can book as late as 2 hours before pickup.',
  },
  {
    q: 'Do you pick up from airports?',
    a: 'Yes — provide your airline and flight number when you book. We track your flight in real time, and your driver meets you curbside at arrivals.',
  },
  {
    q: 'What if I\'m delayed getting out of the airport?',
    a: 'We include 45 minutes of complimentary wait time for domestic flights and 60 minutes for international. After that, it\'s just $0.50 per minute — and we\'ll work with you.',
  },
  {
    q: 'Do you have child seats for airport rides?',
    a: 'Yes — up to 3 child seats per vehicle, pre-installed upon request. Include your child\'s age and weight when you book.',
  },
  {
    q: 'What payment do you accept?',
    a: 'Cash or all major credit cards.',
  },
  {
    q: 'What\'s the cancellation policy for airport rides?',
    a: 'Please cancel at least 4 hours before your scheduled pickup time.',
  },
];

const AIRPORT_SCHEMAS = [
  webPage('Airport Rides from Staten Island', 'Pre-booked airport rides from Staten Island to Newark, JFK, and LaGuardia with real-time flight tracking.', '/airport-rides-staten-island'),
  { '@context': 'https://schema.org', '@type': 'Service', '@id': 'https://goislandride.com/airport-rides-staten-island/#service', name: 'Airport Rides from Staten Island', serviceType: 'Airport transportation', description: 'Pre-booked airport rides from Staten Island to Newark (EWR), JFK, and LaGuardia (LGA). Tolls included. Real-time flight tracking for return pickups.', provider: { '@id': 'https://goislandride.com/#business' }, areaServed: { '@type': 'Place', name: 'Staten Island' }, url: 'https://goislandride.com/airport-rides-staten-island/', offers: [
    { '@type': 'Offer', name: 'Newark Airport (EWR)', priceCurrency: 'USD', price: '70', description: 'Rides to Newark Airport starting at $70. Tolls included.' },
    { '@type': 'Offer', name: 'JFK Airport', priceCurrency: 'USD', price: '100', description: 'Rides to JFK Airport starting at $100. Tolls included.' },
    { '@type': 'Offer', name: 'LaGuardia Airport (LGA)', priceCurrency: 'USD', price: '115', description: 'Rides to LaGuardia Airport starting at $115. Tolls included.' },
  ]},
  breadcrumb('Airport Rides', '/airport-rides-staten-island'),
];

export default function AirportRides() {
  return (
    <>
      <PageSEO
        title="Airport Taxi from Staten Island — JFK, Newark, LaGuardia | Island Ride"
        description="Taxi from Staten Island to JFK, Newark, or LaGuardia. Reliable airport rides with pre-booked pickups and real-time flight tracking. Pre-installed child seats available. Call or WhatsApp Island Ride."
        path="/airport-rides-staten-island"
        schemas={AIRPORT_SCHEMAS}
      />
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="airport-hero">
        <img
          src={HERO_IMG}
          alt="Cars driving on highway at sunset — Island Ride airport taxi service from Staten Island to Newark, JFK, and LaGuardia airports"
          title="Staten Island airport car service"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/60 to-background/20" />
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary"
            data-testid="airport-rides-h1"
          >
            Airport Rides from Staten Island
          </h1>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-lg leading-relaxed">
            Pre-booked rides to Newark, JFK, and LaGuardia. Tolls included, flights tracked, pre-installed child seats.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="airport-cta-request">
              <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" className="bg-background/80" data-testid="airport-cta-call">
              <a href={BUSINESS.phoneTel}><Phone className="h-4 w-4" /><span className="ml-1.5">{BUSINESS.phone}</span></a>
            </Button>
            <Button asChild variant="outline" className="bg-background/80" data-testid="airport-cta-whatsapp">
              <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /><span className="ml-1.5">WhatsApp</span></a>
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-primary text-primary-foreground" data-testid="airport-trust-strip">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-5 sm:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 sm:gap-y-0">
            <div className="flex items-center justify-center gap-3 sm:border-r sm:border-white/20 px-4">
              <CreditCard className="h-5 w-5 shrink-0 text-primary-foreground/80" />
              <span className="font-display text-base sm:text-lg font-semibold tracking-wide">
                Tolls Included
              </span>
            </div>
            <div className="flex items-center justify-center gap-3 sm:border-r sm:border-white/20 px-4">
              <Radar className="h-5 w-5 shrink-0 text-primary-foreground/80" />
              <span className="font-display text-base sm:text-lg font-semibold tracking-wide">
                Real-Time Flight Tracking
              </span>
            </div>
            <div className="flex items-center justify-center gap-3 px-4">
              <Baby className="h-5 w-5 shrink-0 text-primary-foreground/80" />
              <span className="font-display text-base sm:text-lg font-semibold tracking-wide">
                Pre-Installed Child Seats
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Airport Cards */}
      <section className="section-pad" data-testid="airport-cards-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
            data-testid="airport-cards-heading"
          >
            Airports we serve
          </h2>
          <p className="mt-2 text-muted-foreground">
            Estimated drive times from New Dorp. Actual times vary with traffic.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {AIRPORTS.map((a) => (
              <Card
                key={a.key}
                className="p-6 flex flex-col transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:shadow-lg"
                style={{ background: 'linear-gradient(to top, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
                data-testid={`airport-card-${a.key}`}
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary">
                    <Plane className="h-5 w-5" />
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary rounded-full bg-accent/80 px-2.5 py-0.5">
                    <Clock className="h-3 w-3" /> {a.drive}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-lg font-bold">
                  {a.name} <span className="text-muted-foreground font-normal">({a.code})</span>
                </h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed flex-1">{a.desc}</p>
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-display font-bold text-primary">${a.price}</p>
                    <p className="text-[11px] text-muted-foreground">starting at · tolls included</p>
                  </div>
                  <Button asChild size="sm" data-testid={`airport-book-${a.key}`}>
                    <Link to="/request-a-ride">Book<ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Strip */}
      <section className="bg-secondary border-y" data-testid="airport-vehicle-strip">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="flex items-center gap-4" data-testid="vehicle-sedan">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/55 border border-primary/15 shrink-0">
                <Car className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-display text-base sm:text-lg font-bold text-foreground">Sedan</p>
                <p className="text-sm text-muted-foreground">Up to 3 passengers</p>
              </div>
            </div>
            <div className="flex items-center gap-4" data-testid="vehicle-child-seats">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/55 border border-primary/15 shrink-0">
                <Baby className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-display text-base sm:text-lg font-bold text-foreground">Up to 3 Child Seats</p>
                <p className="text-sm text-muted-foreground">Pre-installed upon request</p>
              </div>
            </div>
            <div className="flex items-center gap-4" data-testid="vehicle-minivan">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/55 border border-primary/15 shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-display text-base sm:text-lg font-bold text-foreground">Minivan</p>
                <p className="text-sm text-muted-foreground">Up to 5 passengers with luggage</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Airport Pickup — How It Works */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to bottom, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
        data-testid="airport-pickup-section"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary">
              Airport Pickup
            </h2>
            <p className="mt-3 text-muted-foreground text-base sm:text-lg">
              We pick up from every airport. Just share your flight details.
            </p>
          </div>

          <div className="relative mt-12 sm:mt-16">
            {/* Horizontal connecting line */}
            <div
              aria-hidden="true"
              className="hidden sm:block absolute left-[12.5%] right-[12.5%] top-[84px] h-px bg-gradient-to-r from-primary/0 via-primary/35 to-primary/0"
            />

            <ol className="relative grid grid-cols-1 sm:grid-cols-4 gap-y-14 sm:gap-y-0 sm:gap-x-6">
              {PICKUP_STEPS.map((step, i) => (
                <li key={step.title} className="flex flex-col items-center text-center">
                  <div
                    className="relative w-20 h-20 rounded-2xl bg-accent/55 border border-primary/15 flex items-center justify-center shadow-[var(--shadow-soft)]"
                    data-testid={`pickup-step-${i + 1}-pill`}
                  >
                    <step.icon className="h-8 w-8 text-primary" strokeWidth={1.75} aria-hidden="true" />
                  </div>
                  <span
                    aria-hidden="true"
                    className="hidden sm:block w-2.5 h-2.5 rounded-full bg-primary -mt-[6px] ring-4 ring-background z-10"
                  />
                  <p className="mt-5 sm:mt-6 font-display text-sm font-semibold tracking-[0.22em] text-primary">
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="mt-2 font-display text-lg sm:text-xl font-bold text-foreground max-w-[16rem]">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-[18rem] leading-relaxed">
                    {step.desc}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <p className="mt-12 text-center text-sm text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            We provide 45 minutes of complimentary wait time for domestic flights and 60 minutes for international — no stress through baggage claim or customs. After that, just $0.50 per minute.
          </p>

          <div className="mt-8 flex justify-center">
            <Button asChild data-testid="airport-pickup-cta">
              <Link to="/request-a-ride">Request an airport pickup<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to right, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
        data-testid="airport-faq-section"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
            data-testid="airport-faq-heading"
          >
            Frequently asked questions
          </h2>
          <div className="mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="space-y-2" data-testid="airport-faq">
              {FAQS.map((faq) => (
                <AccordionItem key={faq.q} value={faq.q} className="rounded-lg border bg-background px-5">
                  <AccordionTrigger
                    className="text-sm font-semibold text-left py-4 hover:no-underline"
                    data-testid={`airport-faq-trigger-${faq.q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Cross Links */}
      <section className="bg-secondary border-y" data-testid="airport-cross-links">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Not flying? Check out <Link to="/local-rides-staten-island" className="text-primary font-medium hover:underline">Local Rides</Link> or view our <Link to="/staten-island-service-area" className="text-primary font-medium hover:underline">Service Area</Link>.
            </p>
            <Button asChild variant="outline" size="sm" data-testid="airport-cross-contact">
              <Link to="/contact">Contact us<ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Bottom CTA Strip */}
      <section className="bg-primary text-primary-foreground" data-testid="airport-bottom-cta">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-3.5 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-y-3 gap-x-5 text-center">
            <span className="font-display font-semibold text-sm sm:text-[15px]">Flying soon?</span>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="bottom-cta-call">
                <a href={BUSINESS.phoneTel}><Phone className="h-3.5 w-3.5" /><span className="ml-1.5">Call {BUSINESS.phone}</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="bottom-cta-whatsapp">
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-3.5 w-3.5" /><span className="ml-1.5">WhatsApp</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="bottom-cta-request">
                <Link to="/request-a-ride">Request a ride<ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
